import React, { useContext, useEffect, useMemo } from 'react';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeDishPositionContext,
	IChangeDishPositionContext,
} from '../../../../../changeDishPositionState/changeDishPositionContext';
import { UserRoles, IGetDishPositionChains, IGetDishPositionChainsArguments } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishPositionChains } from 'backend/dishes';
import { useRoleAccess } from 'hooks/useRoleAccess';
import style from './RestaurantMultipleSelect.module.scss';

const RestaurantMultipleSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const {
		setAdditionalRestaurants,
		setFetchedRestaurants,
		IDSelectedRestaurant,
		IDSelectedCategory,
		IDSelectedSubCategory,
		selectedAdditionalRestaurants,
		isApplyToOne,
		isVisibleAdditionalRestaurantsModal,
	} = useContext<IChangeDishPositionContext>(ChangeDishPositionContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<
		IGetDishPositionChains,
		IGetDishPositionChainsArguments
	>(getDishPositionChains, {
		restaurantId: IDSelectedRestaurant || '',
		categoryId: IDSelectedCategory || '',
		subCategoryId: IDSelectedSubCategory || '',
	});

	const additionalRestaurants = useMemo(() => {
		const accessibleRestaurantIDs = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
		const restaurantsFromServer =
			response?.data.restaurantChains.filter((el) => el.id !== IDSelectedRestaurant) || [];
		const intersectedRestaurants = restaurantsFromServer.filter((el) => accessibleRestaurantIDs.includes(el.id));
		return hasAccess ? restaurantsFromServer : intersectedRestaurants;
	}, [response, IDSelectedRestaurant, loggedUser, hasAccess]);

	const handleRemove = (id: string) => {
		const selectedItems = selectedAdditionalRestaurants.filter((el) => el !== id);
		setAdditionalRestaurants(selectedItems);
	};

	useEffect(() => {
		if (response) {
			setFetchedRestaurants(response.data.restaurantChains);
		}
	}, [response]);

	const restaurantsList = useMemo(() => {
		return selectedAdditionalRestaurants.map((el) => {
			const label = additionalRestaurants.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [handleRemove, selectedAdditionalRestaurants, additionalRestaurants]);

	return (
		<div className={style.container} data-test="RestaurantField">
			<div className={style.field}>
				<MultipleSelect
					data={additionalRestaurants}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					isApplyToOne={isApplyToOne}
					setAdditionalRestaurants={setAdditionalRestaurants}
					selectedAdditionalRestaurants={selectedAdditionalRestaurants}
					isVisibleAdditionalRestaurantsModal={isVisibleAdditionalRestaurantsModal}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default RestaurantMultipleSelect;
